<template>
    <AppLayout v-bind="appSharedData.getAppSidebarLayoutProps(pageMeta)">
        <div class="space-y-8">
            <div>
                <div class="text-xl font-semibold mb-4 text-gray-800">
                    My Integrations
                </div>
                <div class="grid grid-cols-12 gap-4">
                    <!-- Google Calendar -->
                    <div class="col-span-12 md:col-span-6 lg:col-span-3">
                        <GoogleCalendarIntegrationCard
                            v-if="
                                isIntegrationConnected(
                                    localCompanyUserIntegrations,
                                    IntegrationProviderEnum.GoogleCalendar,
                                )
                            "
                            :integration="
                                getIntegrationByProvider(
                                    localCompanyUserIntegrations,
                                    IntegrationProviderEnum.GoogleCalendar,
                                )
                            "
                            :providerData="
                                getCompanyUserIntegrationProviderData(
                                    IntegrationProviderEnum.GoogleCalendar,
                                )
                            "
                            @onIntegrationDeleted="
                                handleCompanyUserIntegrationDeleted(
                                    getIntegrationByProvider(
                                        localCompanyUserIntegrations,
                                        IntegrationProviderEnum.GoogleCalendar,
                                    ),
                                )
                            "
                            @onIntegrationUpdated="
                                handleCompanyUserIntegrationUpdated(
                                    $event.integration,
                                )
                            "
                        />
                        <GenericUnconnectedIntegrationCard
                            v-else
                            :providerData="
                                getCompanyUserIntegrationProviderData(
                                    IntegrationProviderEnum.GoogleCalendar,
                                )
                            "
                        />
                    </div>

                    <!-- Outlook Calendar -->
                    <div class="col-span-12 md:col-span-6 lg:col-span-3">
                        <OutlookCalendarIntegrationCard
                            v-if="
                                isIntegrationConnected(
                                    localCompanyUserIntegrations,
                                    IntegrationProviderEnum.OutlookCalendar,
                                )
                            "
                            :integration="
                                getIntegrationByProvider(
                                    localCompanyUserIntegrations,
                                    IntegrationProviderEnum.OutlookCalendar,
                                )
                            "
                            :providerData="
                                getCompanyUserIntegrationProviderData(
                                    IntegrationProviderEnum.OutlookCalendar,
                                )
                            "
                            @onIntegrationDeleted="
                                handleCompanyUserIntegrationDeleted(
                                    getIntegrationByProvider(
                                        localCompanyUserIntegrations,
                                        IntegrationProviderEnum.OutlookCalendar,
                                    ),
                                )
                            "
                            @onIntegrationUpdated="
                                handleCompanyUserIntegrationUpdated(
                                    $event.integration,
                                )
                            "
                        />
                        <GenericUnconnectedIntegrationCard
                            v-else
                            :providerData="
                                getCompanyUserIntegrationProviderData(
                                    IntegrationProviderEnum.OutlookCalendar,
                                )
                            "
                        />
                    </div>
                </div>
            </div>

            <div>
                <div class="text-xl font-semibold mb-4 text-gray-800">
                    Company Integrations
                </div>
                <div class="grid grid-cols-12 gap-4">
                    <!-- Google Meet -->
                    <div class="col-span-12 md:col-span-6 lg:col-span-3">
                        <GenericConnectedIntegrationCard
                            v-if="
                                isIntegrationConnected(
                                    localCompanyIntegrations,
                                    IntegrationProviderEnum.GoogleMeet,
                                )
                            "
                            :integration="
                                getIntegrationByProvider(
                                    localCompanyIntegrations,
                                    IntegrationProviderEnum.GoogleMeet,
                                )
                            "
                            :providerData="
                                getCompanyIntegrationProviderData(
                                    IntegrationProviderEnum.GoogleMeet,
                                )
                            "
                            @onIntegrationDeleted="
                                handleCompanyIntegrationDeleted(
                                    getIntegrationByProvider(
                                        localCompanyIntegrations,
                                        IntegrationProviderEnum.GoogleMeet,
                                    ),
                                )
                            "
                        />
                        <GenericUnconnectedIntegrationCard
                            v-else
                            :providerData="
                                getCompanyIntegrationProviderData(
                                    IntegrationProviderEnum.GoogleMeet,
                                )
                            "
                        />
                    </div>

                    <!-- Zoom -->
                    <div class="col-span-12 md:col-span-6 lg:col-span-3">
                        <GenericConnectedIntegrationCard
                            v-if="
                                isIntegrationConnected(
                                    localCompanyIntegrations,
                                    IntegrationProviderEnum.Zoom,
                                )
                            "
                            :integration="
                                getIntegrationByProvider(
                                    localCompanyIntegrations,
                                    IntegrationProviderEnum.Zoom,
                                )
                            "
                            :providerData="
                                getCompanyIntegrationProviderData(
                                    IntegrationProviderEnum.Zoom,
                                )
                            "
                            @onIntegrationDeleted="
                                handleCompanyIntegrationDeleted(
                                    getIntegrationByProvider(
                                        localCompanyIntegrations,
                                        IntegrationProviderEnum.Zoom,
                                    ),
                                )
                            "
                        />
                        <GenericUnconnectedIntegrationCard
                            v-else
                            :providerData="
                                getCompanyIntegrationProviderData(
                                    IntegrationProviderEnum.Zoom,
                                )
                            "
                        />
                    </div>

                    <!-- Stripe -->
                    <div class="col-span-12 md:col-span-6 lg:col-span-3">
                        <StripeIntegrationCard
                            v-if="
                                isIntegrationConnected(
                                    localCompanyIntegrations,
                                    IntegrationProviderEnum.StripeConnect,
                                )
                            "
                            :integration="
                                getIntegrationByProvider(
                                    localCompanyIntegrations,
                                    IntegrationProviderEnum.StripeConnect,
                                )
                            "
                            :providerData="
                                getCompanyIntegrationProviderData(
                                    IntegrationProviderEnum.StripeConnect,
                                )
                            "
                            @onIntegrationDeleted="
                                handleCompanyIntegrationDeleted(
                                    getIntegrationByProvider(
                                        localCompanyIntegrations,
                                        IntegrationProviderEnum.StripeConnect,
                                    ),
                                )
                            "
                        />
                        <GenericUnconnectedIntegrationCard
                            v-else
                            :providerData="
                                getCompanyIntegrationProviderData(
                                    IntegrationProviderEnum.StripeConnect,
                                )
                            "
                        />
                    </div>

                    <!-- PayPal -->
                    <div
                        v-if="props.hasPayPalFeatureFlag"
                        class="col-span-12 md:col-span-6 lg:col-span-3"
                    >
                        <PayPalIntegrationCard
                            v-if="
                                isIntegrationConnected(
                                    localCompanyIntegrations,
                                    IntegrationProviderEnum.PayPalMultiParty,
                                )
                            "
                            :integration="
                                getIntegrationByProvider(
                                    localCompanyIntegrations,
                                    IntegrationProviderEnum.PayPalMultiParty,
                                )
                            "
                            :providerData="
                                getCompanyIntegrationProviderData(
                                    IntegrationProviderEnum.PayPalMultiParty,
                                )
                            "
                            @onIntegrationDeleted="
                                handleCompanyIntegrationDeleted(
                                    getIntegrationByProvider(
                                        localCompanyIntegrations,
                                        IntegrationProviderEnum.PayPalMultiParty,
                                    ),
                                )
                            "
                        />
                        <GenericUnconnectedIntegrationCard
                            v-else
                            :providerData="
                                getCompanyIntegrationProviderData(
                                    IntegrationProviderEnum.PayPalMultiParty,
                                )
                            "
                        />
                    </div>
                </div>
            </div>
        </div>

        <PageBottomPadding />
    </AppLayout>
</template>

<script setup lang="ts">
    import { onBeforeMount, ref } from 'vue'
    import Integration from '@/App/Integration/Entity/Integration'
    import AppLayout from '@/Shared/Layout/App/Sidebar/AppLayout.vue'
    import {
        IntegrationProviderEnum,
        type IntegrationProviderDataType,
    } from '@/Shared/AutoGenerated/Main'
    import GenericConnectedIntegrationCard from '@/App/Integration/Component/GenericConnectedIntegrationCard.vue'
    import GenericUnconnectedIntegrationCard from '@/App/Integration/Component/GenericUnconnectedIntegrationCard.vue'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import type { PageMeta } from '@/Shared/AutoGenerated/Main'
    import AppSharedData from '@/Shared/Entity/AppSharedData'
    import PageBottomPadding from '@/Shared/Layout/App/Sidebar/PageBottomPadding.vue'
    import StripeIntegrationCard from '@/App/Integration/Component/StripeIntegrationCard.vue'
    import GoogleCalendarIntegrationCard from '@/App/Integration/Component/GoogleCalendarIntegrationCard.vue'
    import OutlookCalendarIntegrationCard from '@/App/Integration/Component/OutlookCalendarIntegrationCard.vue'
    import { RoutePathAppEnum } from '@/Shared/AutoGenerated/RoutePathEnum'
    import PayPalIntegrationCard from '@/App/Integration/Component/PayPalIntegrationCard.vue'

    interface IndexIntegrationsProps {
        pageMeta: PageMeta
        appSharedDataData: any
        companyIntegrationsData: any
        companyUserIntegrationsData: any
        companyIntegrationProviders: IntegrationProviderDataType[]
        companyUserIntegrationProviders: IntegrationProviderDataType[]
        preSelectedProvider: string | null
        hasPayPalFeatureFlag: boolean
    }

    const props = defineProps<IndexIntegrationsProps>()
    const appSharedData = AppSharedData.hydrate(props.appSharedDataData)
    const localCompanyIntegrations = ref<Integration[]>(
        Integration.hydrateMultiple(props.companyIntegrationsData),
    )
    const localCompanyUserIntegrations = ref<Integration[]>(
        Integration.hydrateMultiple(props.companyUserIntegrationsData),
    )

    const isIntegrationConnected = (
        integrations: Integration[],
        providerKey: IntegrationProviderEnum,
    ): boolean => {
        return !!integrations.find((integration: Integration) => {
            return integration.provider === providerKey
        })
    }

    const getIntegrationByProvider = (
        integrations: Integration[],
        providerKey: IntegrationProviderEnum,
    ): Integration => {
        const integration = integrations.find((integration: Integration) => {
            return integration.provider === providerKey
        })

        if (!integration) {
            throw new Error(`Missing integration for provider ${providerKey}`)
        }

        return integration
    }

    const getCompanyUserIntegrationProviderData = (
        providerKey: IntegrationProviderEnum,
    ): IntegrationProviderDataType => {
        return getIntegrationProviderData(
            props.companyUserIntegrationProviders,
            providerKey,
        )
    }

    const getCompanyIntegrationProviderData = (
        providerKey: IntegrationProviderEnum,
    ): IntegrationProviderDataType => {
        return getIntegrationProviderData(
            props.companyIntegrationProviders,
            providerKey,
        )
    }

    const getIntegrationProviderData = (
        providers: IntegrationProviderDataType[],
        providerKey: IntegrationProviderEnum,
    ): IntegrationProviderDataType => {
        const integrationProviderData = providers.find(
            (integration: IntegrationProviderDataType) => {
                return integration.key === providerKey
            },
        )

        if (!integrationProviderData) {
            throw new Error(`Missing integration for provider ${providerKey}`)
        }

        return integrationProviderData
    }

    const handleCompanyIntegrationDeleted = (
        deletedIntegration: Integration,
    ): void => {
        localCompanyIntegrations.value = localCompanyIntegrations.value.filter(
            (integration: Integration) => {
                return integration.id.value !== deletedIntegration.id.value
            },
        )
    }

    const handleCompanyUserIntegrationDeleted = (
        deletedIntegration: Integration,
    ): void => {
        localCompanyUserIntegrations.value =
            localCompanyUserIntegrations.value.filter(
                (integration: Integration) => {
                    return integration.id.value !== deletedIntegration.id.value
                },
            )
    }

    const handleCompanyUserIntegrationUpdated = (
        updatedIntegration: Integration,
    ): void => {
        localCompanyUserIntegrations.value =
            localCompanyUserIntegrations.value.map(
                (integration: Integration) => {
                    if (integration.id.value !== updatedIntegration.id.value) {
                        return integration
                    }

                    return updatedIntegration
                },
            )
    }

    onBeforeMount(() => {
        if (!props.preSelectedProvider) {
            return
        }

        // Removes the query string from the URL
        const cleanUrl = new UrlFactory().makeIntegrationsPageUrl()
        history.replaceState(null, 'null', cleanUrl)
    })
</script>
